import { createFormatForDescription as cffd } from '../createFormatForDescription'
import explainAmount from '../explain/explainAmount'
import {
  createPaymentPathExplanation,
  PaymentPathExplanation,
} from '../explain/ExplainPaymentPath'
import {
  Explanation,
  explain,
  explainOptional,
  explainOptionalArray,
} from '../explain/Explanation'
import { MatchFlag, matchFlags, Flag } from '../flag'
import PaymentInstructions from '../types/instructions/PaymentInstructions'
import TransactionType from '../types/TransactionType'
import CurrencyAmount from '../types/util/CurrencyAmount'

/**
 * An annotation for payment transactions.
 *
 * @property type - An explanation of the type.
 * @property additionalFlags - Any transaction-type specific flags.
 * @property amount - An explanation of the amount.
 * @property destination - An explanation of the destination.
 * @property destinationTag - An explanation of the destinationTag.
 * @property invoiceID - An explanation of the invoiceID.
 * @property paths - An explanation of the paths.
 * @property sendMax - An explanation of the sendMax.
 * @property deliverMin - An explanation of the deliverMin.
 */
export interface PaymentAnnotation {
  readonly type: TransactionType.Payment
  readonly additionalFlags: Flag[]

  readonly amount: Explanation<CurrencyAmount>
  readonly destination: Explanation<string>
  readonly destinationTag?: Explanation<number>
  readonly invoiceID?: Explanation<string>
  readonly paths?: Explanation<PaymentPathExplanation[]>
  readonly sendMax?: Explanation<CurrencyAmount>
  readonly deliverMin?: Explanation<CurrencyAmount>
}

enum PaymentFlag {
  PartialPayment = 'tfPartialPayment',
  NoDirectRipple = 'tfNoDirectRipple',
  LimitQuality = 'tfLimitQuality',
}

const extraFlags: MatchFlag[] = [
  {
    code: 0x00020000,
    name: PaymentFlag.PartialPayment,
    label:
      'If the specified Amount cannot be sent without spending more than SendMax, reduce the received amount instead of failing outright.',
  },
  {
    code: 0x00010000,
    name: PaymentFlag.NoDirectRipple,
    label:
      'Do not use the default path; only use paths included in the Paths field. This is intended to force the transaction to take arbitrage opportunities. Most clients do not need this.',
  },
  {
    code: 0x00040000,
    name: PaymentFlag.LimitQuality,
    label:
      'Only take paths where all the conversions have an input:output ratio that is equal or better than the ratio of Amount:SendMax.',
  },
]

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * Calls the describe function for BaseAnnotation.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: PaymentInstructions,
): PaymentAnnotation {
  const additionalFlags = matchFlags(transactionInstructions.Flags, extraFlags)

  const isPartialPayment: boolean =
    additionalFlags.find((flag) => flag.name === PaymentFlag.PartialPayment) !==
    undefined

  // TODO: check that this explanation is correct.
  const amount = explain(
    transactionInstructions.Amount,
    `Deliver ${isPartialPayment ? 'up to' : ''} ${explainAmount(
      transactionInstructions.Amount,
    )} to the destination.`,
  )

  const destination = explain(
    transactionInstructions.Destination,
    `The funds will be sent to address ${cffd(
      transactionInstructions.Destination,
      'destination',
    )}`,
  )

  const destinationTag = explainOptional(
    transactionInstructions.DestinationTag,
    (tag) =>
      `This tag identifies a hosted recipient or other information for the recipient of the deleted account's leftover XRP: ${cffd(
        tag.toString(),
        'destinationTag',
      )}`,
  )

  const invoiceID = explainOptional(
    transactionInstructions.InvoiceID,
    (invoice) =>
      `The reason for this transaction is given by hash ${cffd(
        invoice,
        'invoiceID',
      )}`,
  )

  const paths = explainOptionalArray(
    transactionInstructions.Paths,
    createPaymentPathExplanation,
    'The paths array specify a way for issued currency to intermediary steps on their way from sender to receiver. Each element is a possible way for currency to travel.',
  )

  const sendMax = explainOptional(
    transactionInstructions.SendMax,
    (max) => `This transaction can send at most ${explainAmount(max)}`,
  )

  const deliverMin = explainOptional(
    transactionInstructions.DeliverMin,
    (min) => `This transaction must send at least ${explainAmount(min)}`,
  )

  return {
    additionalFlags,
    amount,
    destination,
    ...(destinationTag && { destinationTag }),
    ...(invoiceID && { invoiceID }),
    ...(paths && { paths }),
    ...(sendMax && { sendMax }),
    ...(deliverMin && { deliverMin }),

    type: TransactionType.Payment,
  }
}
